import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircle, faCalendarCheck, faClock } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { getSlotChangeInvite, submitSlotChangeRequest } from '../../utils/slotChangeService';
import { friendlyServerError } from '../../utils/formValidation';
import { formatTime12Hour, getDayName } from '../../utils/schedulingUtils';
import '../Register/RegisterStudent.css';
import './ChangeTime.css';

// Public, token-keyed lesson-time change form (2026-09-16).
//
// An existing parent lands here from the link staff emailed from the student
// profile. The page shows the student's current weekly time and the open pool,
// the parent picks every time that could work, and the pick becomes a
// registrationRequests doc the admin assigns from the Registration tab.
// Checking a time never books it; the copy says so twice on purpose.
//
// Same picker vocabulary as /register step 2 (`.rg-*` classes from
// RegisterStudent.css) so a family who registered recognises it; the shell is
// the single-column `.register-page` card rather than step 2's two-column
// layout, because there is nothing to sell here.

const SLOT_LOCATION_SHORT = {
	HLDC: 'Burnsville',
	Lakeville: 'Lakeville',
};
const slotLocationShort = (loc) => SLOT_LOCATION_SHORT[loc] || loc;

export const describeSlot = (slot) =>
	`${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} in ${slotLocationShort(slot.location)}`;

const ChangeTime = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';

	const [status, setStatus] = useState(token ? 'loading' : 'invalid'); // loading | ready | invalid | error
	const [invite, setInvite] = useState(null);
	const [loadError, setLoadError] = useState('');

	const [picked, setPicked] = useState([]);
	const [flexible, setFlexible] = useState(false);
	const [notes, setNotes] = useState('');
	const [slotFilter, setSlotFilter] = useState('all');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [attn, setAttn] = useState(false);
	const [done, setDone] = useState(false);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		(async () => {
			try {
				const data = await getSlotChangeInvite(token);
				if (cancelled) return;
				setInvite(data);
				setStatus('ready');
			} catch (err) {
				if (cancelled) return;
				if (err?.code === 'functions/not-found') {
					setStatus('invalid');
				} else {
					setLoadError(friendlyServerError(err, "We couldn't load the open times. Please try again."));
					setStatus('error');
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token]);

	const toggle = (id) =>
		setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

	const canSubmit = picked.length > 0 || flexible;

	const submit = async () => {
		// Never the only guard on a wa-*/native control: the handler checks too.
		if (!canSubmit) {
			setAttn(true);
			return;
		}
		if (submitting) return;
		setSubmitting(true);
		setError('');
		try {
			await submitSlotChangeRequest({ token, requestedSlotIds: picked, openToAnything: flexible, notes });
			setDone(true);
			window.scrollTo({ top: 0 });
		} catch (err) {
			setError(friendlyServerError(err, "We couldn't send that. Please try again."));
		} finally {
			setSubmitting(false);
		}
	};

	const shell = (title, body) => (
		<main className='register-page cyv change-time'>
			<Helmet defer={false}>
				<title>{title} | Color Your Voice</title>
				<meta name='robots' content='noindex,nofollow' />
			</Helmet>
			<div className='register-card'>
				<div className='register-inner'>{body}</div>
			</div>
		</main>
	);

	if (status === 'loading') {
		return shell('Pick a lesson time', <p className='register-slots-status'>Loading open times…</p>);
	}
	if (status === 'error') {
		return shell(
			'Pick a lesson time',
			<Alert variant='danger' className='register-error'>
				{loadError}{' '}
				<button type='button' className='register-retry' onClick={() => window.location.reload()}>
					Try again
				</button>
			</Alert>
		);
	}
	if (status === 'invalid' || !invite) {
		return shell(
			'Link not valid',
			<div className='register-success'>
				<h1 className='register-success__title'>This link isn't valid.</h1>
				<p className='register-success__body'>
					It may have been copied incompletely. Reply to the email we sent you and we'll
					send a fresh one.
				</p>
			</div>
		);
	}

	const student = invite.studentFirstName || 'your student';
	const current = invite.currentSlots || [];

	if (done) {
		return shell(
			'Request received',
			<div className='register-success'>
				<FontAwesomeIcon icon={faCircleCheck} className='register-success__icon' />
				<h1 className='register-success__title'>Got it. We'll confirm {student}'s new time by email.</h1>
				<p className='register-success__body'>
					Nothing has changed yet: {student}'s current lesson time stays as it is until you
					hear from us. We've also sent you a copy of what you picked.
				</p>
				<Link to='/' className='btn-cyv-teal'>
					Back to home
				</Link>
			</div>
		);
	}

	if (invite.state !== 'open') {
		const copy =
			invite.state === 'submitted'
				? {
						title: 'You already sent us your picks.',
						body: `We have your request for ${student} and will confirm the new time by email. Need to change something? Just reply to our email.`,
				  }
				: {
						title: 'This link has expired.',
						body: 'Open times move quickly, so these links only last two weeks. Reply to the email we sent you and we will send a fresh one.',
				  };
		return shell(
			copy.title,
			<div className='register-success'>
				<h1 className='register-success__title'>{copy.title}</h1>
				<p className='register-success__body'>{copy.body}</p>
			</div>
		);
	}

	const slots = invite.openSlots || [];
	const visible = slotFilter === 'all' ? slots : slots.filter((s) => s.location === slotFilter);
	const byDay = visible.reduce((acc, s) => {
		(acc[s.dayOfWeek] = acc[s.dayOfWeek] || []).push(s);
		return acc;
	}, {});
	const dayOrder = Object.keys(byDay)
		.map(Number)
		.sort((a, b) => a - b);
	const countLabel =
		picked.length === 0
			? flexible
				? 'Any open time could work'
				: 'No times picked yet'
			: `${picked.length} time${picked.length === 1 ? '' : 's'} picked${flexible ? ', and any could work' : ''}`;

	return (
		<main className='register-page cyv change-time'>
			<Helmet defer={false}>
				<title>Pick a new lesson time | Color Your Voice</title>
				<meta name='robots' content='noindex,nofollow' />
			</Helmet>

			<div className='register-card'>
				<div className='register-inner'>
					<header className='rg-head'>
						<span className='eyebrow'>Lesson time change</span>
						<h1 className='rg-title'>Which times could work for {student}?</h1>
						<p className='rg-sub'>
							These are our open weekly lesson times right now. Pick every one that
							could work. Picking a time doesn't book it: we'll confirm the new time by
							email, and {student}'s current time stays put until then.
						</p>
						{current.length > 0 && (
							<div className='rg-pills'>
								{current.map((s) => (
									<span className='rg-pill' key={s.id}>
										<FontAwesomeIcon icon={faCalendarCheck} className='rg-pill__i rg-pill__i--teal' />
										Current time: {describeSlot(s)}
									</span>
								))}
								<span className='rg-pill'>
									<FontAwesomeIcon icon={faClock} className='rg-pill__i rg-pill__i--orange' />
									One 45-minute lesson each week
								</span>
							</div>
						)}
					</header>

					{error && (
						<Alert variant='danger' className='register-error'>
							{error}
						</Alert>
					)}

					{slots.length > 0 && (
						<div className='rg-fchips' role='group' aria-label='Filter by location'>
							{[
								{ key: 'all', label: 'All locations' },
								{ key: 'Lakeville', label: 'Lakeville' },
								{ key: 'HLDC', label: 'Burnsville' },
							].map((f) => (
								<button
									key={f.key}
									type='button'
									className={`rg-fchip${slotFilter === f.key ? ' rg-fchip--on' : ''}`}
									onClick={() => setSlotFilter(f.key)}>
									{f.label}
								</button>
							))}
						</div>
					)}

					<div className={`rg-card rg-formpad${attn && !canSubmit ? ' rg-card--attn' : ''}`}>
						{slots.length === 0 ? (
							<p className='register-slots-status'>
								Nothing is open right now. Check "Any open time could work" below and we'll
								reach out as soon as something frees up.
							</p>
						) : visible.length === 0 ? (
							<p className='register-slots-status'>
								No open times at this location right now. Try the other location, or check
								"Any open time could work" below.
							</p>
						) : (
							dayOrder.map((day) => (
								<div className='rg-day' key={day}>
									<div className='rg-dayhead'>
										<span className='rg-dayname'>{getDayName(day)}</span>
										<span className='rg-dayline' />
									</div>
									<div className='rg-slots'>
										{byDay[day].map((slot) => {
											const on = picked.includes(slot.id);
											return (
												<button
													type='button'
													key={slot.id}
													className={`rg-chip${on ? ' rg-chip--on' : ''}`}
													onClick={() => toggle(slot.id)}
													aria-pressed={on}>
													<span className='rg-chip__time'>
														<FontAwesomeIcon icon={on ? faCircleCheckSolid : faCircle} />
														{formatTime12Hour(slot.startTime)}
													</span>
													<span className='rg-chip__loc'>{slotLocationShort(slot.location)}</span>
												</button>
											);
										})}
									</div>
								</div>
							))
						)}

						<div className='rg-hatches'>
							<button
								type='button'
								className={`rg-hatch${flexible ? ' rg-hatch--on' : ''}`}
								onClick={() => setFlexible((v) => !v)}
								aria-pressed={flexible}>
								<span className='rg-hbox'>
									<FontAwesomeIcon icon={faCheck} />
								</span>
								<span className='rg-hatch__txt'>
									<span className='rg-hatch__label'>Any open time could work</span>
									<span className='rg-hatch__sub'>We'll suggest the best fit.</span>
								</span>
							</button>
						</div>
						<p className={`rg-count${attn && !canSubmit ? ' rg-count--attn' : ''}`} aria-live='polite'>
							{countLabel}
						</p>
					</div>

					<div className='rg-card rg-notescard'>
						<label htmlFor='ct-notes' className='rg-noteslabel'>
							Anything we should know? <span className='register-opt'>(optional)</span>
						</label>
						<textarea
							id='ct-notes'
							rows={3}
							className='rg-notesinput'
							placeholder='A first choice, when the change should start, anything that helps…'
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</div>

					<div className='rg-actions change-time__actions'>
						<div className='rg-continuewrap'>
							<button type='button' className='btn-cyv-teal' onClick={submit} disabled={submitting}>
								{submitting ? 'Sending…' : 'Send my picks'}
							</button>
							<span className={`rg-hint${attn && !canSubmit ? ' rg-hint--attn' : ''}`} aria-live='polite'>
								{canSubmit
									? 'We confirm by email. Nothing changes until then.'
									: 'Pick at least one time, or check "Any open time could work".'}
							</span>
						</div>
					</div>
				</div>
			</div>
		</main>
	);
};

export default ChangeTime;
